import type {
    SurveyQuestion,
    SurveyQuestionValidation,
    SurveyValidationNormalizeRule,
} from '@/api/surveys';
import type { InputHTMLAttributes } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DIGITS_ONLY_REGEX = /\D/g;
const DATE_BR_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DATE_ISO_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const isBlankValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
};

const isValidStepValue = (value: number, min: number, step: number) => {
    const quotient = (value - min) / step;
    return Math.abs(quotient - Math.round(quotient)) < 0.000001;
};

const normalizeString = (
    value: string,
    normalizeRules?: SurveyValidationNormalizeRule[],
): string => {
    if (!normalizeRules?.length) return value;

    return normalizeRules.reduce((acc, rule) => {
        switch (rule) {
            case 'trim':
                return acc.trim();
            case 'lowercase':
                return acc.toLowerCase();
            case 'digits_only':
                return acc.replace(DIGITS_ONLY_REGEX, '');
            default:
                return acc;
        }
    }, value);
};

const isValidCpf = (value: string): boolean => {
    const cpf = value.replace(DIGITS_ONLY_REGEX, '');
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    const calcCheckDigit = (base: string, factorStart: number) => {
        let total = 0;
        for (let i = 0; i < base.length; i += 1) {
            total += Number(base[i]) * (factorStart - i);
        }
        const mod = (total * 10) % 11;
        return mod === 10 ? 0 : mod;
    };

    const firstDigit = calcCheckDigit(cpf.slice(0, 9), 10);
    const secondDigit = calcCheckDigit(cpf.slice(0, 10), 11);

    return firstDigit === Number(cpf[9]) && secondDigit === Number(cpf[10]);
};

const validateRegex = (value: string, regex?: string): boolean => {
    if (!regex) return true;

    try {
        return new RegExp(regex).test(value);
    } catch {
        return true;
    }
};

type ParsedDate = {
    day: number;
    month: number;
    year: number;
};

const isLeapYear = (year: number) => {
    if (year % 400 === 0) return true;
    if (year % 100 === 0) return false;
    return year % 4 === 0;
};

const isValidCalendarDate = ({ day, month, year }: ParsedDate) => {
    if (year < 1000 || year > 9999) return false;
    if (month < 1 || month > 12) return false;

    const maxDayByMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    return day >= 1 && day <= maxDayByMonth[month - 1];
};

const parseSupportedDateInput = (value: string): ParsedDate | null => {
    const trimmed = value.trim();

    const brMatch = trimmed.match(DATE_BR_REGEX);
    if (brMatch) {
        const parsed = {
            day: Number(brMatch[1]),
            month: Number(brMatch[2]),
            year: Number(brMatch[3]),
        };
        return isValidCalendarDate(parsed) ? parsed : null;
    }

    const isoMatch = trimmed.match(DATE_ISO_REGEX);
    if (isoMatch) {
        const parsed = {
            day: Number(isoMatch[3]),
            month: Number(isoMatch[2]),
            year: Number(isoMatch[1]),
        };
        return isValidCalendarDate(parsed) ? parsed : null;
    }

    return null;
};

const formatDateAsBR = ({ day, month, year }: ParsedDate) => {
    const dd = String(day).padStart(2, '0');
    const mm = String(month).padStart(2, '0');
    const yyyy = String(year);
    return `${dd}/${mm}/${yyyy}`;
};

const formatDateAsISO = ({ day, month, year }: ParsedDate) => {
    const dd = String(day).padStart(2, '0');
    const mm = String(month).padStart(2, '0');
    const yyyy = String(year);
    return `${yyyy}-${mm}-${dd}`;
};

const normalizeSurveyAnswerValue = (value: unknown, validation?: SurveyQuestionValidation) => {
    if (typeof value === 'string') {
        const normalizedValue = validation?.normalize?.length
            ? normalizeString(value, validation.normalize)
            : value;

        if (validation?.style === 'date') {
            const parsedDate = parseSupportedDateInput(normalizedValue);
            if (parsedDate) return formatDateAsBR(parsedDate);
        }

        return normalizedValue;
    }

    if (Array.isArray(value)) {
        if (!validation?.normalize?.length) return value;
        return value.map((item) =>
            typeof item === 'string' ? normalizeString(item, validation.normalize) : item,
        );
    }

    return value;
};

export const normalizeSurveyAnswers = (
    questions: SurveyQuestion[],
    answers: Record<string, unknown>,
): Record<string, unknown> => {
    const normalized = { ...answers };

    questions.forEach((question) => {
        const currentValue = normalized[question.field_name];
        normalized[question.field_name] = normalizeSurveyAnswerValue(
            currentValue,
            question.validation,
        );
    });

    return normalized;
};

export const formatSurveyValueForInput = (
    value: unknown,
    validation?: SurveyQuestionValidation,
) => {
    if (typeof value !== 'string') return value;
    if (validation?.style !== 'date') return value;

    const parsedDate = parseSupportedDateInput(value);
    if (!parsedDate) return value;

    return formatDateAsISO(parsedDate);
};

const getValidationMessage = (validation: SurveyQuestionValidation | undefined, fallback: string) =>
    validation?.message || fallback;

export const validateSurveyQuestionValue = (
    question: SurveyQuestion,
    value: unknown,
): string | undefined => {
    const normalizedValue = normalizeSurveyAnswerValue(value, question.validation);
    const validation = question.validation;
    const questionType = question.field_type?.toLowerCase();
    const isCheckboxQuestion = questionType === 'checkbox';

    if (isCheckboxQuestion) {
        if (question.required && normalizedValue !== true) {
            return getValidationMessage(validation, 'Campo obrigatório');
        }
        return undefined;
    }

    if (question.required && isBlankValue(normalizedValue)) {
        return getValidationMessage(validation, 'Campo obrigatório');
    }

    if (!question.required && isBlankValue(normalizedValue)) {
        return undefined;
    }

    const style = validation?.style || questionType;

    if (style === 'multiselect') {
        if (!Array.isArray(normalizedValue)) {
            return getValidationMessage(validation, 'Formato inválido');
        }

        if (question.options?.length) {
            const allowedValues = question.options.map((option) =>
                typeof option === 'string' ? option : option.value,
            );
            const allValid = normalizedValue.every((item) => allowedValues.includes(String(item)));

            if (!allValid) {
                return getValidationMessage(validation, 'Seleção inválida');
            }
        }

        return undefined;
    }

    if (style === 'select' && question.options?.length) {
        const current = String(normalizedValue);
        const allowedValues = question.options.map((option) =>
            typeof option === 'string' ? option : option.value,
        );

        if (!allowedValues.includes(current)) {
            if (question.config?.allow_other) return undefined;
            return getValidationMessage(validation, 'Seleção inválida');
        }
    }

    if (questionType === 'textarea' && typeof normalizedValue !== 'string') {
        return getValidationMessage(validation, 'Formato inválido');
    }

    if (questionType === 'rating') {
        const rating = question.config?.rating;
        const numericValue = Number(normalizedValue);

        if (
            !rating ||
            Number.isNaN(numericValue) ||
            numericValue < rating.min ||
            numericValue > rating.max ||
            !isValidStepValue(numericValue, rating.min, rating.step)
        ) {
            return getValidationMessage(validation, 'Valor inválido');
        }

        return undefined;
    }

    const stringValue = String(normalizedValue);

    switch (style) {
        case 'email':
            if (!EMAIL_REGEX.test(stringValue)) {
                return getValidationMessage(validation, 'E-mail inválido');
            }
            break;
        case 'phone': {
            const digits = stringValue.replace(DIGITS_ONLY_REGEX, '');
            if (digits.length < 10 || digits.length > 11) {
                return getValidationMessage(validation, 'Telefone inválido');
            }
            break;
        }
        case 'cpf':
            if (!isValidCpf(stringValue)) {
                return getValidationMessage(validation, 'CPF inválido');
            }
            break;
        case 'date': {
            const parsedDate = parseSupportedDateInput(stringValue);
            if (!parsedDate) {
                return getValidationMessage(validation, 'Data inválida');
            }
            break;
        }
        case 'number':
            if (Number.isNaN(Number(stringValue))) {
                return getValidationMessage(validation, 'Número inválido');
            }
            break;
        default:
            break;
    }

    if (!validateRegex(stringValue, validation?.regex)) {
        return getValidationMessage(validation, 'Formato inválido');
    }

    return undefined;
};

export const resolveTextInputType = (
    validation?: SurveyQuestionValidation,
): InputHTMLAttributes<HTMLInputElement>['type'] => {
    if (validation?.style === 'email') return 'email';
    if (validation?.style === 'date') return 'date';
    return 'text';
};

export const resolveInputMode = (
    validation?: SurveyQuestionValidation,
): InputHTMLAttributes<HTMLInputElement>['inputMode'] => {
    if (validation?.input_mode) return validation.input_mode;

    switch (validation?.style) {
        case 'email':
            return 'email';
        case 'phone':
            return 'tel';
        case 'cpf':
        case 'number':
            return 'numeric';
        default:
            return 'text';
    }
};
